import { Box } from "@material-ui/core";
import { useFeatureFlagMonitor } from "@nzfm/app-config";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { Product } from "../../../api";
import { Client, FLAG_SKIP_NCC_SCREEN, RouteScreen } from "../../../common/types";
import { useAppSelector } from "../../../store";
import ClientDetails from "../../application/ClientDetails";
import DescriptionOfConnections from "../../application/DescriptionOfConnections";
import KiwiSaverOnly from "../../application/KiwiSaverOnly";
import ProductDetails from "../../application/ProductDetails";
import ApplicationDetails from "./steps/ApplicationDetails";
import ConfirmDetails from "./steps/ConfirmDetails";
import SelectProducts from "./steps/SelectProducts";

export default ({ history, location, match }: RouteComponentProps) => {
  const { client } = useAppSelector((state) => state.adviser);
  const { nccList } = useAppSelector((state) => state.clientApplication);
  const { enabled: skipNccScreenEnabled } = useFeatureFlagMonitor(FLAG_SKIP_NCC_SCREEN);

  const nextScreen = (previousScreen: boolean, client: Client) => {
    const direction = previousScreen ? -1 : 1;
    const currentIndex = currentScreen ? screens.indexOf(currentScreen) : -1;
    let i = currentIndex + direction;
    let screen = screens[i];

    while (screen && screen.skipRoute && screen.skipRoute(client)) {
      i += direction;
      screen = screens[i];
    }

    return screen;
  };

  const generateRoute = (route?: string) => `/adviser/new-application/${!route ? "" : route}`;

  const navigate = (screen: RouteScreen) => {
    if (!!screen) history.push(generateRoute(screen.route));
  };

  const prevRoute = (client: Client) => {
    navigate(nextScreen(true, client));
  };

  const nextRoute = (client: Client) => {
    navigate(nextScreen(false, client));
  };

  const screens: RouteScreen[] = [
    {
      Component: SelectProducts,
      title: "Product selection",
      header: "Select products",
      route: "select-products",
      nextRoute,
    },
    {
      Component: KiwiSaverOnly,
      header: "KiwiSaver",
      title: "KiwiSaver",
      route: "kiwisaver",
      prevRoute,
      nextRoute,
      skipRoute: (client: Client) => !(client.products?.length === 1 && client.products.includes(Product.KiwiSaver)),
    },
    {
      Component: ApplicationDetails,
      title: "New Application",
      header: "Application",
      route: "application-details",
      nextRoute,
      prevRoute,
    },
    {
      Component: ClientDetails,
      header: "Client",
      title: "Client",
      route: "client-details",
      prevRoute,
      nextRoute,
      skipRoute: (client: Client) => (client.products?.length === 1 && client.products.includes(Product.KiwiSaver)) || !client.prepopulated,
    },
    ...(skipNccScreenEnabled
      ? []
      : [
          {
            Component: DescriptionOfConnections,
            header: "Description of connections",
            title: "Description of connections",
            route: "description-of-connections",
            prevRoute,
            nextRoute,
            skipRoute: (client: Client) =>
              (client.products?.length === 1 && client.products.includes(Product.KiwiSaver)) ||
              !client.prepopulated ||
              (!nccList!.includes(client.countryOfBirth || "") && !!client.citizenships && !client.citizenships.find((c) => nccList!.includes(c))),
          },
        ]),
    {
      Component: ProductDetails,
      header: "Products",
      title: "Products",
      route: "product-details",
      prevRoute,
      nextRoute,
      skipRoute: (client: Client) => (client.products?.length === 1 && client.products.includes(Product.KiwiSaver)) || !client.prepopulated,
    },
    {
      Component: ConfirmDetails,
      header: "Confirm details",
      title: "Confirm details",
      route: "confirm-details",
      prevRoute,
      nextRoute,
      skipRoute: (client: Client) => client.products?.length === 1 && client.products.includes(Product.KiwiSaver),
    },
  ];

  const currentScreen = (() => screens.find(({ route }) => route === location.pathname.split("/")[3]))();

  return (
    <Box px={11} py={3}>
      <Switch>
        {screens
          .filter((screen) => !!client || screen.route === "select-products")
          .map(({ Component, ...props }) => (
            <Route exact key={props.route} path={match.path + (props.route ? "/" + props.route : "")} render={(routeProps) => <Component {...routeProps} {...props} />} />
          ))}
        <Route>
          <Redirect to={generateRoute("select-products")} />
        </Route>
      </Switch>
    </Box>
  );
};
